// The window control's arithmetic — which whole contract weeks a reading covers.
//
// Pure, and deliberately store-free and React-free: the SERVER computes the
// metrics for a window and the CLIENT renders the picker for it, so the one
// definition of "the last 4 weeks" has to be importable by both. Same reason
// lib/persona-scope.ts exists, and the same failure if it did not — two answers
// to one question, disagreeing on a Sunday.
//
// EVERYTHING IS UTC AND EVERYTHING IS A CALENDAR DATE, exactly as
// lib/schedule-dates.ts is, and this file reuses its arithmetic rather than
// repeating it. A window boundary is a day on a wall calendar, not an instant:
// "the week of Aug 24" is the same seven days for an AM in London and one in
// Los Angeles, and a window that shifted by timezone would move a script from
// one bar of the chart to the next depending on who opened the page.
//
// WINDOWS SNAP TO WHOLE WEEKS, SUN-SAT (the convention schedule-dates.ts uses
// everywhere), never to a rolling 7 days. Every number on the throughput page is
// a weekly-contract number, and a rolling window cuts the current week in half —
// which makes "approved vs committed" read as a permanent shortfall that nobody
// can fix, because the missing days have not happened yet.

import { boundedMemo } from "@/lib/bounded-memo";
import { addDaysUtc, dowOf, formatShort } from "@/lib/schedule-dates";

/**
 * The three readings. Weeks, not months — "past 3 months" is 12 or 13 weeks
 * depending on who is counting, and the app's own vocabulary is weekly
 * everywhere else (`week_date`, week numbers, the 4-week schedule grid).
 *
 * The URL carries these strings verbatim (`?win=4w`), so they are a wire format:
 * renaming one breaks every link anybody has pasted into Slack.
 *
 * ⚠️ THERE IS NO ONE-WEEK WINDOW, AND THERE USED TO BE.
 * `this` was kept at first because it was the natural window for the Scripts
 * tab; that tab became a row popup which ignores the window entirely, and what
 * is left of "what landed this week" is the LAST COLUMN of the week-by-week
 * table — one week read against the eleven beside it, which is more than a tile
 * with nothing to compare itself to could ever say. A pasted `?win=this` is now
 * junk, and junk is the same as absence: `parseWin` answers null and the reader
 * lands on the default window rather than on a window that no longer exists.
 */
export type Win = "4w" | "12w" | "all";

/** Display order — the segmented control's, left to right, narrowest first. */
export const WINDOWS: Win[] = ["4w", "12w", "all"];

export const WINDOW_LABELS: Record<Win, string> = {
  "4w": "4 weeks",
  "12w": "12 weeks",
  all: "All",
};

/**
 * What a KPI delta is measured AGAINST, in words — and null for `all`, which has
 * nothing to be measured against.
 *
 * It names the window rather than its dates on purpose. Windows snap to whole
 * Sun-Sat weeks, so "the previous 4 weeks" is exact rather than approximate, and
 * a caption naming two ISO dates would be a third place the boundary arithmetic
 * is written down — free to disagree with the axis above it the day the
 * arithmetic moves.
 */
export const PREVIOUS_WINDOW_LABELS: Record<Win, string | null> = {
  "4w": "the previous 4 weeks",
  "12w": "the previous 12 weeks",
  all: null,
};

/**
 * How many whole weeks each window spans, INCLUDING the current one — or null
 * for `all`, which has no span of its own and takes one from the data (see
 * `weekStartsFrom`).
 *
 * The span COUNTS THE CURRENT WEEK WHOLE, Sunday through Saturday. Days later in
 * the week that have not happened yet are still inside the window, which is what
 * makes a mid-week "12 of 60 approved" read as progress rather than as failure.
 */
const SPAN: Record<Win, number | null> = { "4w": 4, "12w": 12, all: null };

/**
 * The window a page shows when the URL does not name one.
 *
 * ⚠️ AND THE ONLY FALLBACK THERE IS. No `?win=` means you are on this window;
 * changing the window changes the URL to match, so there is no per-viewer
 * memory anywhere and nothing that could disagree with the address bar (a
 * per-viewer localStorage memory was dropped for this).
 */
export const DEFAULT_WIN: Win = "4w";

/**
 * A `?win=` value, or null if it is not one of ours.
 *
 * NULL RATHER THAN THE DEFAULT, so the caller writes `parseWin(x) ?? DEFAULT_WIN`
 * and the substitution happens somewhere you can see it. A junk param is the
 * same as no param, which is the honest reading of a link somebody truncated.
 */
export function parseWin(value: string | null | undefined): Win | null {
  return WINDOWS.includes(value as Win) ? (value as Win) : null;
}

/**
 * The SUNDAY of the week containing `iso`, as `YYYY-MM-DD`.
 *
 * The one primitive every other function here is built from: a window is a run
 * of week starts, a bucket is the week start of the day something happened on,
 * and "which column does this belong in" is this function twice.
 *
 * MEMOISED: it is asked of the same few hundred dates tens of
 * thousands of times a render, and the answer depends on the string alone, UTC,
 * so it is safe on the server and in the browser. The memo is bounded
 * (lib/bounded-memo.ts). A string that is not `YYYY-MM-DD` answers itself, as
 * `addDaysUtc` always has.
 */
export const weekStartOf: (iso: string) => string = boundedMemo((iso) => addDaysUtc(iso, -dowOf(iso)));

/** The Saturday that closes the week starting on `sundayIso`. */
export function weekEndOf(sundayIso: string): string {
  return addDaysUtc(sundayIso, 6);
}

/**
 * The window's whole-week bounds, both INCLUSIVE and both calendar dates — or
 * null for `all`, which has no lower bound.
 *
 * `to` is the SATURDAY OF THE CURRENT WEEK, never today: see SPAN above. A date
 * comparison against these is a plain string compare, because `YYYY-MM-DD` sorts
 * chronologically — no parsing, no timezone, nothing to get wrong at a month
 * boundary.
 */
export function windowRange(win: Win, todayIso: string): { from: string; to: string } | null {
  const span = SPAN[win];
  const thisWeek = weekStartOf(todayIso);
  if (span === null) return null;
  return { from: addDaysUtc(thisWeek, -7 * (span - 1)), to: weekEndOf(thisWeek) };
}

/**
 * Is this calendar date inside the window?
 *
 * ⚠️ `all` HAS NO LOWER BOUND AND STILL HAS AN UPPER ONE — it is "everything up
 * to the close of this week", never "every date there is". Two reasons, and the
 * first is arithmetic: `weekStartsIn` clamps the axis to the current week, so a
 * date in the future belongs to NO column, and a window that counted it would
 * report a total its own columns could not add up to.
 *
 * The second is the header's whole argument. Batches are created about two weeks before
 * they go out, so there are always future ones in the store; counting what they
 * owe against approvals that cannot exist yet is precisely the permanent,
 * unfixable shortfall that snapping to whole weeks exists to prevent.
 */
export function inWindow(iso: string, win: Win, todayIso: string): boolean {
  if (iso > weekEndOf(weekStartOf(todayIso))) return false;
  const range = windowRange(win, todayIso);
  return !range || iso >= range.from;
}

/**
 * WHICH DATES A READING COUNTS, as a value rather than a closure: a range of
 * calendar dates, or one Sun–Sat week.
 *
 * A dashboard asks this of every fact once per column, and a closure over
 * `inWindow` re-derived the window's bounds on every ask. Built once, it is two
 * string compares, or one compare of a week start the caller worked out when it
 * loaded the fact. `hits` IS `inWindow`, `inPreviousWindow`, an explicit
 * inclusive range and `weekStartOf(iso) === week`: window.test.ts checks it
 * against each of them, day by day.
 */
export type DateFilter =
  | { kind: "range"; from: string | null; to: string }
  | { kind: "week"; week: string };

/** `inWindow(·, win, today)`: no lower bound for `all`, and the close of this week above. */
export function windowFilter(win: Win, todayIso: string): DateFilter {
  return { kind: "range", from: windowRange(win, todayIso)?.from ?? null, to: weekEndOf(weekStartOf(todayIso)) };
}

/** `inPreviousWindow(·, win, today)`, or null for `all`, which has no window before it. */
export function previousWindowFilter(win: Win, todayIso: string): DateFilter | null {
  const range = previousRange(win, todayIso);
  return range && { kind: "range", from: range.from, to: range.to };
}

/** Both bounds inclusive, as a Weekly Report's brand week is. */
export const rangeFilter = (range: { from: string; to: string }): DateFilter => ({ kind: "range", from: range.from, to: range.to });

/** The one Sun–Sat week starting on `sundayIso`, matched by week start. */
export const weekFilter = (sundayIso: string): DateFilter => ({ kind: "week", week: sundayIso });

/**
 * Does a date fall inside the filter? `week` MUST be `weekStartOf(on)`: the
 * caller passes it in because it worked it out once, beside the date.
 */
export function hits(filter: DateFilter, on: string, week: string): boolean {
  return filter.kind === "week" ? week === filter.week : on <= filter.to && (filter.from === null || on >= filter.from);
}

/**
 * Every week start in the window, oldest first — the columns of the week-by-week
 * table, and the buckets the metric layer groups into.
 *
 * `all` HAS NO SPAN OF ITS OWN, so it takes one from the data: `earliestIso` is
 * the oldest thing that happened in the current scope, and the window runs from
 * that week to this one. Passing nothing (a scope with no dated events at all)
 * yields just the current week rather than an empty axis — a table with no
 * columns is a rendering bug looking for a place to happen, and "this week, and
 * nothing in it" is the true answer.
 */
export function weekStartsIn(win: Win, todayIso: string, earliestIso?: string | null): string[] {
  const thisWeek = weekStartOf(todayIso);
  const from = windowRange(win, todayIso)?.from ?? weekStartOf(earliestIso || todayIso);
  // A stored date in the FUTURE — a post date set for next month — must not make
  // the axis run backwards. Clamped, and the event still lands in no column,
  // which is the same answer every out-of-window event gets.
  const start = from < thisWeek ? from : thisWeek;

  const weeks: string[] = [];
  for (let w = start; w <= thisWeek; w = addDaysUtc(w, 7)) weeks.push(w);
  return weeks;
}

/**
 * A week start as its column heading: "2026-08-24" → "Aug 24".
 *
 * The same short form the Videos page uses for a post date, on purpose — the
 * column heading and the week it names should read alike.
 *
 * ⚠️ IT WAS DAY-FIRST ("24 Aug") AND IS NOT ANY MORE. The
 * argument was that twelve of these sit across a table where only the day
 * changes between neighbours, so a repeated month hides the part that moves.
 * That is true in the abstract and did not survive being read: the header row
 * scanned as a SECOND DATE FORMAT rather than as an aid, and two date orders in
 * one app is the larger cost.
 *
 * ⚠️ IT IS A NAMED FUNCTION RATHER THAN A BARE `formatShort` CALL, even though
 * it now returns exactly that. The heading has a role, and a role with a name
 * has one place to change — which is the whole reason the day-first experiment
 * cost three lines instead of three call sites.
 */
export const weekLabel = (sundayIso: string): string => formatShort(sundayIso);

/**
 * The window immediately preceding `win`, of equal length — both bounds
 * INCLUSIVE calendar dates — or null for `all`, which has none.
 *
 * ⚠️ `all` HAS NO PRECEDING WINDOW, and that is a fact about the window rather
 * than a gap to fill in. It has no lower bound, so it has no length to step
 * back by: "the four weeks before all of time" is not a date range, and any
 * range invented for it would be a comparison the reader could not check. The
 * page says so rather than rendering an ambiguous blank.
 *
 * The upper bound here is a HARD one — the day before the current window opens —
 * where `inWindow`'s is the close of this week. That asymmetry is the point: the
 * current window runs to the end of the week we are in, and the previous one
 * stops dead where it starts, so nothing is counted in both.
 */
export function previousRange(win: Win, todayIso: string): { from: string; to: string } | null {
  const span = SPAN[win];
  const current = windowRange(win, todayIso);
  if (span === null || !current) return null;
  return { from: addDaysUtc(current.from, -7 * span), to: addDaysUtc(current.from, -1) };
}

/**
 * Is this calendar date inside the window BEFORE the one being read?
 *
 * The delta on a KPI tile is this predicate against the same scope — one more
 * reading of the same corpus, never a second query.
 */
export function inPreviousWindow(iso: string, win: Win, todayIso: string): boolean {
  const range = previousRange(win, todayIso);
  return !!range && iso >= range.from && iso <= range.to;
}
